import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '@/app/store';
import { setTokens, clearTokens } from '@/features/auth';
import { getDeviceIdHeader } from '@/shared/auth/deviceId';
import { removePosition, syncInList } from '@/features/cart/lib/cartCache';
import { withQuery } from './queryString';
import type {
  ExchangeEsiaCodeResponse,
  PrescriptionGroup,
  PrescriptionDetail,
  User,
  FaqItem,
  Region,
  Relative,
  RelativeCreateBody,
  RelativeUpdateBody,
  Settings,
  SettingsParams,
  Order,
  OrderStatusType,
  Chat,
  ChatMessage,
  Pharmacy,
  PharmacyMapPin,
  PharmaciesListParams,
  PharmaciesMapParams,
  ProductListItem,
  ProductSearchItem,
  ProductSearchParams,
  ProductFilterQuery,
  ProductFilterValues,
  ProductFilterValuesParams,
  ProductsSearchParams,
  SuggestionsResponse,
  SuggestionsParams,
  PharmacyOffer,
  PharmacyOffersPage,
  PharmacyOfferMapPin,
  PharmacyOffersParams,
  PharmacyOffersMapParams,
  PharmacySearchMapPin,
  PharmacySearchMapParams,
  PharmacyProductOffer,
  Cart,
  CartPositionCreateBody,
  CartPositionUpdateBody,
  CartPositionRemoveParams,
} from '@/shared/types';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api',
  responseHandler: async (response) => {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  },
});

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const token = state.login.accessToken;

  let modifiedArgs: FetchArgs =
    typeof args === 'string' ? { url: args } : { ...args };

  const isTokenEndpoint = modifiedArgs.url === '/v4/auth/token';

  const deviceIdHeader = getDeviceIdHeader();
  const deviceIdHeaders = deviceIdHeader
    ? { 'X-Device-Id': deviceIdHeader }
    : {};

  if (!isTokenEndpoint && token) {
    modifiedArgs.headers = {
      ...deviceIdHeaders,
      ...(modifiedArgs.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  } else {
    modifiedArgs.headers = {
      ...deviceIdHeaders,
      ...(modifiedArgs.headers || {}),
    };
  }

  let result = await rawBaseQuery(modifiedArgs, api, extraOptions);

  if (result.error?.status === 401 && !isTokenEndpoint) {
    const refreshToken = state.login.refreshToken;

    if (refreshToken) {
      const refreshResult = await rawBaseQuery(
        {
          url: '/v4/auth/token',
          method: 'POST',
          headers: deviceIdHeaders,
          body: {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
          },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const data = refreshResult.data as {
          access_token: string;
          refresh_token?: string;
        };

        api.dispatch(
          setTokens({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          })
        );

        modifiedArgs = {
          ...modifiedArgs,
          headers: {
            ...deviceIdHeaders,
            ...(modifiedArgs.headers || {}),
            Authorization: `Bearer ${data.access_token}`,
          },
        };

        result = await rawBaseQuery(modifiedArgs, api, extraOptions);
      } else {
        api.dispatch(clearTokens());
      }
    } else {
      api.dispatch(clearTokens());
    }
  }

  return result;
};

// Типы заданы вручную: вывод через AppDispatch зациклил бы store на api
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: any) => unknown;

type Patch = { undo: () => void };

type MutationLifecycle<T> = {
  dispatch: Dispatch;
  queryFulfilled: Promise<{ data: T }>;
};

/** Патч кэша с возможностью отката */
const patchCache = (dispatch: Dispatch, thunk: unknown): Patch =>
  dispatch(thunk) as Patch;

/**
 * Мутации корзины возвращают её целиком, поэтому раскладываем ответ по кэшу
 * сами, вместо инвалидации: иначе за теми же данными уходит второй запрос.
 */
const applyCartResponse = (dispatch: Dispatch, cart: Cart): void => {
  dispatch(api.util.upsertQueryData('getPharmacyCart', cart.pharmacyId, cart));
  dispatch(
    api.util.updateQueryData('getCarts', undefined, (draft) =>
      syncInList(draft, cart)
    )
  );
};

const saveCartResponse = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: MutationLifecycle<Cart>
): Promise<void> => {
  const { data } = await queryFulfilled;
  applyCartResponse(dispatch, data);
};

/** Позиция исчезает из корзины сразу — ответа ждать секунды */
const removeCartPositionOptimistic = async (
  { pharmacyId, positionId }: CartPositionRemoveParams,
  { dispatch, queryFulfilled }: MutationLifecycle<Cart>
): Promise<void> => {
  const patch = patchCache(
    dispatch,
    api.util.updateQueryData('getPharmacyCart', pharmacyId, (draft) =>
      removePosition(draft, positionId)
    )
  );

  try {
    const { data } = await queryFulfilled;
    applyCartResponse(dispatch, data);
  } catch {
    patch.undo();
    // Патч мог разойтись с сервером, если параллельно шла другая правка
    dispatch(api.util.invalidateTags([{ type: 'Carts', id: pharmacyId }]));
  }
};

/** Список «Мои корзины» и бейдж правим сразу: карточка не должна зависать */
const dropCarts = (dispatch: Dispatch, keep: (cart: Cart) => boolean): Patch =>
  patchCache(
    dispatch,
    api.util.updateQueryData('getCarts', undefined, (draft) =>
      draft.filter((cart) => keep(cart))
    )
  );

const patchCartsCount = (
  dispatch: Dispatch,
  next: (count: number) => number
): Patch =>
  patchCache(
    dispatch,
    api.util.updateQueryData('getCartsCount', undefined, (draft) => {
      draft.count = next(draft.count);
    })
  );

const undoAll = (patches: Patch[]): void => {
  patches.reverse().forEach((patch) => patch.undo());
};

/** Удаление корзины одной аптеки со списка «Мои корзины» */
const clearPharmacyCartOptimistic = async (
  pharmacyId: string,
  { dispatch, queryFulfilled }: MutationLifecycle<Cart>
): Promise<void> => {
  const patches = [
    dropCarts(dispatch, (cart) => cart.pharmacyId !== pharmacyId),
    patchCartsCount(dispatch, (count) => Math.max(0, count - 1)),
  ];

  try {
    await queryFulfilled;
  } catch {
    undoAll(patches);
  }
};

/** Удаление всех корзин разом */
const clearAllCartsOptimistic = async (
  _arg: void,
  { dispatch, queryFulfilled }: MutationLifecycle<Cart[]>
): Promise<void> => {
  const patches = [
    dropCarts(dispatch, () => false),
    patchCartsCount(dispatch, () => 0),
  ];

  try {
    await queryFulfilled;
  } catch {
    undoAll(patches);
  }
};

export const api = createApi({
  reducerPath: 'api',
  tagTypes: ['Relatives', 'Orders', 'Prescriptions', 'Carts'],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getEsiaAuthUrl: builder.query<{ url: string }, { redirectURI: string }>({
      query: ({ redirectURI }) => ({
        url: '/v4/auth/esia',
        params: { redirectURI, external: true },
      }),
    }),

    getCurrentUser: builder.query<User, void>({
      query: () => '/v3/users/current',
    }),

    getPrescriptions: builder.query<PrescriptionGroup[], void>({
      query: () => '/v4/prescriptions',
      providesTags: ['Prescriptions'],
    }),

    getPrescription: builder.query<PrescriptionDetail, string>({
      query: (id) => `/v4/prescriptions/${id}`,
    }),

    getPrescriptionQrPayload: builder.query<{ payload: string }, string>({
      query: (id) => `/v4/prescriptions/${id}/qr-code/payload`,
    }),

    getFaq: builder.query<FaqItem[], void>({
      query: () => '/v3/faq',
      transformResponse: (response: FaqItem[]) =>
        [...response].sort((a, b) => a.position - b.position),
    }),

    getRegions: builder.query<Region[], { search?: string }>({
      query: ({ search } = {}) => ({
        url: '/v4/regions',
        params: { ...(search ? { search } : {}), limit: 100 },
      }),
    }),

    getRegionByCoords: builder.query<
      { name: string; regionId: string; regionFias: string },
      { latitude: number; longitude: number }
    >({
      query: ({ latitude, longitude }) => ({
        url: '/v5/address/region/suggested',
        params: { latitude, longitude },
      }),
    }),

    updateUser: builder.mutation<User, { address: { regionFiasId: string } }>({
      query: (body) => ({
        url: '/v4/users',
        method: 'PATCH',
        body,
      }),
    }),

    exchangeEsiaCode: builder.mutation<
      ExchangeEsiaCodeResponse,
      { code: string }
    >({
      query: ({ code }) => ({
        url: '/v4/auth/token',
        method: 'POST',
        body: {
          grant_type: 'authorization_code',
          code,
        },
      }),
    }),

    createDevice: builder.mutation<
      void,
      {
        deviceId: string;
        type: string;
        version?: string;
        pushToken?: string;
        pushProvider?: string;
        pushTokenIsExpired?: boolean;
      }
    >({
      query: (body) => ({
        url: '/v4/devices',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Логаут: отзывает токены на сервере, отвязывает корзину и снимает
     * с девайса пуш-подписку. Та же ручка, что у мобильных приложений
     */
    logout: builder.mutation<void, { deviceId: string }>({
      query: ({ deviceId }) => ({
        url: '/v4/auth/logout',
        params: { deviceId },
      }),
    }),

    // ── Support chat ─────────────────────────────────────────────

    getChats: builder.query<Chat[], void>({
      query: () => '/v3/chats',
    }),

    createSupportChat: builder.mutation<Chat, void>({
      query: () => ({
        url: '/v3/chats/support',
        method: 'POST',
      }),
    }),

    getChatMessages: builder.query<
      ChatMessage[],
      { chatId: string; limit?: number; skip?: number }
    >({
      query: ({ chatId, limit = 30, skip = 0 }) => ({
        url: `/v3/chats/${chatId}/messages`,
        params: { limit, skip, sort: '-creationDate' },
      }),
    }),

    sendMessage: builder.mutation<
      ChatMessage,
      { chatId: string; message: string }
    >({
      query: ({ chatId, message }) => ({
        url: `/v3/chats/${chatId}/messages`,
        method: 'POST',
        body: { message },
      }),
    }),

    sendAttachment: builder.mutation<
      ChatMessage,
      { chatId: string; file: File }
    >({
      query: ({ chatId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/v3/chats/${chatId}/attachments`,
          method: 'POST',
          body: formData,
        };
      },
    }),

    // ── Relatives ─────────────────────────────────────────────────

    getRelatives: builder.query<Relative[], void>({
      query: () => '/v4/relatives',
      providesTags: ['Relatives'],
    }),

    createRelative: builder.mutation<Relative, RelativeCreateBody>({
      query: (body) => ({
        url: '/v4/relatives',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Relatives', 'Prescriptions'],
    }),

    updateRelative: builder.mutation<
      Relative,
      { linkId: string } & RelativeUpdateBody
    >({
      query: ({ linkId, ...body }) => ({
        url: `/v4/relatives/${linkId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Relatives'],
    }),

    deleteUser: builder.mutation<{ _id: string }, void>({
      query: () => ({
        url: '/v4/users',
        method: 'DELETE',
      }),
    }),

    deleteRelative: builder.mutation<void, { linkId: string }>({
      query: ({ linkId }) => ({
        url: `/v4/relatives/${linkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Relatives', 'Prescriptions'],
    }),

    /** Настройки приложения; часть значений зависит от переданного региона */
    getSettings: builder.query<Settings, SettingsParams>({
      query: ({ regionFiasId }) => ({
        url: '/v5/settings',
        params: { ...(regionFiasId ? { regionFiasId } : {}) },
      }),
    }),

    // ── Pharmacies (Ближайшие аптеки) ──────────────────────

    /** Список аптек с гео-сортировкой, поиском и пагинацией */
    getPharmacies: builder.query<Pharmacy[], PharmaciesListParams>({
      query: ({
        regionFiasId,
        search,
        latitude,
        longitude,
        limit,
        skip,
        sort,
        canProcessOrders,
        canProcessPrescriptions,
        canProcessPrivilege,
        aroundTheClock,
        IsPickupPoint,
      }) => ({
        url: '/v4/pharmacies',
        params: {
          regionFiasId,
          // Роуты patient пробрасывают query в catalog-service целиком,
          // поиск по названию и адресу разбирается уже там
          ...(search ? { search } : {}),
          // Координаты задают и порядок выдачи: с ними catalog-service
          // сортирует по расстоянию, без них — по названию
          ...(latitude != null && longitude != null
            ? { latitude, longitude }
            : {}),
          ...(limit != null ? { limit } : {}),
          ...(skip != null ? { skip } : {}),
          ...(sort ? { sort } : {}),
          ...(canProcessOrders ? { canProcessOrders: true } : {}),
          ...(canProcessPrescriptions ? { canProcessPrescriptions: true } : {}),
          ...(canProcessPrivilege ? { canProcessPrivilege: true } : {}),
          ...(aroundTheClock ? { aroundTheClock: true } : {}),
          ...(IsPickupPoint ? { IsPickupPoint: true } : {}),
        },
      }),
    }),

    /** Лёгкие пины для карты (id + координаты + лого) */
    getPharmaciesOnMap: builder.query<PharmacyMapPin[], PharmaciesMapParams>({
      query: ({
        regionFiasId,
        search,
        canProcessOrders,
        canProcessPrescriptions,
        canProcessPrivilege,
        aroundTheClock,
        IsPickupPoint,
      }) => ({
        url: '/v4/pharmacies/map',
        params: {
          regionFiasId,
          ...(search ? { search } : {}),
          ...(canProcessOrders ? { canProcessOrders: true } : {}),
          ...(canProcessPrescriptions ? { canProcessPrescriptions: true } : {}),
          ...(canProcessPrivilege ? { canProcessPrivilege: true } : {}),
          ...(aroundTheClock ? { aroundTheClock: true } : {}),
          ...(IsPickupPoint ? { IsPickupPoint: true } : {}),
        },
      }),
    }),

    /** Детальная карточка аптеки (с расписанием) */
    getPharmacy: builder.query<Pharmacy, string>({
      query: (pharmacyId) => `/v4/pharmacies/${pharmacyId}`,
    }),
    // ── Orders ────────────────────────────────────────────────────

    getOrder: builder.query<Order, string>({
      query: (orderId) => `/v4/orders/${orderId}`,
    }),

    getOrders: builder.query<
      Order[],
      { limit: number; skip: number; statuses?: OrderStatusType[] }
    >({
      // Набор статусов, а не один: список делится на текущие и завершённые,
      // и каждая вкладка — это несколько статусов сразу
      query: ({ limit, skip, statuses }) =>
        withQuery('/v4/orders', { limit, skip, statuses }),
      providesTags: ['Orders'],
    }),

    /**
     * Черновик заказа по корзине аптеки. Уходит в Kafka, ответ приходит
     * событием шины — запрос висит до ответа.
     */
    createOrderDraft: builder.mutation<Order, { pharmacyId: string }>({
      query: (body) => ({
        url: '/v4/orders',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Формирование заказа. Запрос блокирующий: BFF ждёт ответа аптеки через
     * Kafka до 100 секунд. Теги не инвалидируем — иначе замороженная корзина
     * пропадёт из списка посреди ожидания; делаем это вручную по завершении.
     */
    formOrder: builder.mutation<Order, string>({
      query: (orderId) => ({
        url: `/v4/orders/${orderId}`,
        method: 'POST',
      }),
    }),

    /**
     * Ждёт ответа shopping-service через Kafka — до нескольких секунд.
     * Возвращает заказ в CANCELLING: CANCELLED ставит аптека
     */
    cancelOrder: builder.mutation<Order, string>({
      query: (orderId) => ({
        url: `/v4/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Orders'],
      async onQueryStarted(orderId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            api.util.updateQueryData('getOrder', orderId, () => data)
          );
        } catch {
          // Ошибку покажет errorMiddleware, заказ перечитает экран
        }
      },
    }),

    // ── Каталог ───────────────────────────────────────────────────

    /**
     * Поиск товаров. Строка поиска уходит параметром name — на бэке
     * api-query-params складывает её в filter[name].
     * pharmacyId ограничивает выдачу остатками одной аптеки и заодно
     * подменяет minPrice на цену этой аптеки.
     */
    searchProducts: builder.query<ProductListItem[], ProductsSearchParams>({
      query: ({ regionFiasId, name, pharmacyId, limit, skip, sort }) => ({
        url: '/v4/products',
        params: {
          regionFiasId,
          limit,
          skip,
          ...(name ? { name } : {}),
          ...(pharmacyId ? { pharmacyId } : {}),
          ...(sort ? { sort } : {}),
        },
      }),
    }),

    /**
     * Каталожный поиск товаров. В отличие от searchProducts на /v4/products,
     * отдаёт число аптек по каждому товару и листается честно: эластик
     * возвращает весь набор совпадений, а страницу нарезает база.
     */
    searchProductsWithCount: builder.query<
      ProductSearchItem[],
      ProductSearchParams
    >({
      // Строка собирается вручную: фильтр уходит вложенными параметрами,
      // а штатный сериализатор RTK Query вложенность не умеет
      query: ({ regionFiasId, search, pharmacyId, limit, skip, sort, filter }) =>
        withQuery('/v5/products/search', {
          regionFiasId,
          search,
          limit,
          skip,
          pharmacyId,
          sort,
          filter,
        }),
    }),

    /**
     * Значения фильтров под текущую выдачу: формы, дозировки,
     * производители со странами и границы цены — каждое со счётчиком
     */
    getProductFilterValues: builder.query<
      ProductFilterValues,
      ProductFilterValuesParams
    >({
      query: ({ regionFiasId, pharmacyId, filter }) =>
        withQuery('/v5/products/filter', {
          regionFiasId,
          pharmacyId,
          filter,
        }),
    }),

    /** Подсказки для строки поиска: тексты + топ товаров */
    getProductSuggestions: builder.query<SuggestionsResponse, SuggestionsParams>(
      {
        query: ({ regionFiasId, search }) => ({
          url: '/v4/products/suggestions',
          params: { regionFiasId, search },
        }),
      }
    ),

    /** Карточка товара: тот же формат, что и элемент выдачи, плюс инструкции */
    getProduct: builder.query<
      ProductListItem,
      { productId: string; regionFiasId: string }
    >({
      query: ({ productId, regionFiasId }) => ({
        url: `/v4/products/${productId}`,
        params: { regionFiasId },
      }),
    }),

    // ── Аптеки по строке поиска ───────────────────────────────────

    /**
     * Карта аптек по названию препарата: на пине минимальная цена среди
     * найденных в этой аптеке товаров. Поиск выполняет catalog-service,
     * сюда уходит только строка — набор идентификаторов не поместился бы
     * в query-строку.
     */
    getPharmaciesMapBySearch: builder.query<
      PharmacySearchMapPin[],
      PharmacySearchMapParams
    >({
      // Вложенный filter собираем вручную: штатный сериализатор превратил бы
      // его в «[object Object]» — см. queryString. Разворачиваем в литерал:
      // у интерфейса нет неявной индексной подписи, и сборщик его не примет
      query: (params) => withQuery('/v5/products/pharmacies/map', { ...params }),
    }),

    /** Найденные товары одной аптеки — содержимое шторки пина */
    getPharmacyProductsBySearch: builder.query<
      PharmacyProductOffer[],
      {
        pharmacyId: string;
        regionFiasId: string;
        search: string;
        /** Те же свойства препарата, что у карты */
        filter?: ProductFilterQuery;
      }
    >({
      query: ({ pharmacyId, ...params }) =>
        withQuery(`/v5/products/pharmacies/${pharmacyId}`, { ...params }),
    }),

    // ── Аптеки по товару ──────────────────────────────────────────

    /**
     * Места получения товара: аптеки и пункты выдачи вперемешку.
     * Вместе со страницей приезжают счётчики для чипов — по всей выборке
     */
    getProductPharmacies: builder.query<
      PharmacyOffersPage,
      PharmacyOffersParams
    >({
      query: ({ productId, ...params }) => ({
        url: `/v5/products/${productId}/pharmacies`,
        params,
      }),
    }),

    /** Пины карты с ценой товара в каждой аптеке */
    getProductPharmaciesOnMap: builder.query<
      PharmacyOfferMapPin[],
      PharmacyOffersMapParams
    >({
      query: ({ productId, upperRight, lowerLeft, ...params }) => ({
        url: `/v5/products/${productId}/pharmacies/map`,
        // Углы раскладываем в скобочную нотацию вручную: RTK Query кладёт
        // значения в query как есть, и вложенный объект превратился бы
        // в «[object Object]». На бэке qs соберёт их обратно в объект
        params: {
          ...params,
          ...(upperRight && lowerLeft
            ? {
                'upperRight[latitude]': upperRight.latitude,
                'upperRight[longitude]': upperRight.longitude,
                'lowerLeft[latitude]': lowerLeft.latitude,
                'lowerLeft[longitude]': lowerLeft.longitude,
              }
            : {}),
        },
      }),
      keepUnusedDataFor: 300,
    }),

    /** Карточка аптеки под конкретный товар — для bottom sheet */
    getProductPharmacy: builder.query<
      PharmacyOffer,
      { productId: string; pharmacyId: string }
    >({
      query: ({ productId, pharmacyId }) =>
        `/v5/products/${productId}/pharmacies/${pharmacyId}`,
    }),

    // ── Мультикорзина (v5) ────────────────────────────────────────
    //
    // Теги: id аптеки — корзина этой аптеки, 'LIST' — экран «Мои корзины».
    // Правки позиций не инвалидируют ни то, ни другое: мутация возвращает
    // корзину целиком, и applyCartResponse кладёт её и в корзину, и в список.
    // Остаётся счётчик бейджа — его из ответа одной корзины не посчитать.

    /** Все корзины пользователя, сгруппированные по аптекам */
    getCarts: builder.query<Cart[], void>({
      query: () => '/v5/cart/list',
      providesTags: (result) => [
        { type: 'Carts' as const, id: 'LIST' },
        ...(result ?? []).map(({ pharmacyId }) => ({
          type: 'Carts' as const,
          id: pharmacyId,
        })),
      ],
    }),

    /** Число корзин для бейджа: список ради одной цифры звать слишком дорого */
    getCartsCount: builder.query<{ count: number }, void>({
      query: () => '/v5/cart/count',
      providesTags: [{ type: 'Carts', id: 'COUNT' }],
    }),

    /** Корзина одной аптеки */
    getPharmacyCart: builder.query<Cart, string>({
      query: (pharmacyId) => ({
        url: '/v5/cart',
        params: { pharmacyId },
      }),
      providesTags: (_result, _error, pharmacyId) => [
        { type: 'Carts', id: pharmacyId },
      ],
    }),

    /**
     * Добавление товара в корзину выбранной аптеки.
     * Корзину создаёт сам, если для этой аптеки её ещё нет.
     */
    addCartPosition: builder.mutation<Cart, CartPositionCreateBody>({
      query: (body) => ({
        url: '/v5/cart/positions',
        method: 'POST',
        body,
      }),
      // Тег своей корзины оставлен намеренно: экран корзины открывается, не
      // дожидаясь ответа, и его GET может разойтись по времени с этим POST.
      // Рефетч после ответа гарантирует, что победит свежий состав
      invalidatesTags: (_result, _error, { pharmacyId }) => [
        { type: 'Carts', id: 'COUNT' },
        { type: 'Carts', id: pharmacyId },
      ],
      onQueryStarted: saveCartResponse,
    }),

    /** Изменение количества. pharmacyId обязателен, иначе бэк ответит 400 */
    updateCartPosition: builder.mutation<Cart, CartPositionUpdateBody>({
      query: ({ positionId, amount, pharmacyId }) => ({
        url: `/v5/cart/positions/${positionId}`,
        method: 'PATCH',
        body: { amount, pharmacyId },
      }),
      invalidatesTags: [{ type: 'Carts', id: 'COUNT' }],
      onQueryStarted: saveCartResponse,
    }),

    /** Удаление позиции */
    removeCartPosition: builder.mutation<Cart, CartPositionRemoveParams>({
      query: ({ positionId, pharmacyId }) => ({
        url: `/v5/cart/positions/${positionId}`,
        method: 'DELETE',
        params: { pharmacyId },
      }),
      invalidatesTags: [{ type: 'Carts', id: 'COUNT' }],
      onQueryStarted: removeCartPositionOptimistic,
    }),

    /** Удаление корзины одной аптеки целиком */
    clearPharmacyCart: builder.mutation<Cart, string>({
      query: (pharmacyId) => ({
        url: '/v5/cart',
        method: 'DELETE',
        params: { pharmacyId },
      }),
      invalidatesTags: (_result, _error, pharmacyId) => [
        { type: 'Carts', id: 'LIST' },
        { type: 'Carts', id: 'COUNT' },
        { type: 'Carts', id: pharmacyId },
      ],
      onQueryStarted: clearPharmacyCartOptimistic,
    }),

    /** Удаление всех корзин пользователя разом */
    clearAllCarts: builder.mutation<Cart[], void>({
      query: () => ({
        url: '/v5/cart/all',
        method: 'DELETE',
      }),
      invalidatesTags: ['Carts'],
      onQueryStarted: clearAllCartsOptimistic,
    }),

    /**
     * Слияние анонимных корзин с корзинами пользователя после логина.
     * Сливает попарно по каждой аптеке и сразу возвращает готовый список.
     */
    mergeCarts: builder.mutation<Cart[], void>({
      query: () => ({
        url: '/v5/cart',
        method: 'POST',
      }),
      invalidatesTags: ['Carts'],
    }),
  }),
});
